import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Flame, Sparkles, User, Volume2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { trackClick, tutoringUrl } from "../lib/api";
import Logo from "./Logo";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const linkClass = ({ isActive }) =>
        `text-base font-medium transition-colors ${
            isActive ? "text-[#2D5D4E]" : "text-[#515B57] hover:text-[#2D5D4E]"
        }`;

    return (
        <header className="nav-blur sticky top-0 z-40 border-b border-[#E5E7E2]">
            <div className="container-page flex items-center justify-between h-16">
                <Link to={user ? "/app" : "/"} className="flex items-center gap-2" data-testid="nav-logo">
                    <Logo size={36} showTagline={!user} />
                    <span
                        className="hidden sm:inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E0EDE6] text-[#1F4237]"
                        data-testid="nav-free-pill"
                        title="Lift Up Reading is free for everyone"
                    >
                        Free
                    </span>
                </Link>

                {user ? (
                    <nav className="flex items-center gap-6">
                        {(user.assessment_result || user.role === "admin") ? (
                            <>
                                <NavLink to="/app" end className={linkClass} data-testid="nav-dashboard">Home</NavLink>
                                <NavLink to="/app/curriculum" className={linkClass} data-testid="nav-curriculum">Lessons</NavLink>
                                <NavLink to="/app/games" className={linkClass} data-testid="nav-games">Games</NavLink>
                                <NavLink to="/app/tutor" className={linkClass} data-testid="nav-tutor">Tutor</NavLink>
                                {user.role === "admin" && (
                                    <NavLink to="/app/admin/analytics" className={linkClass} data-testid="nav-admin">Admin</NavLink>
                                )}
                                <div className="hidden md:flex items-center gap-3 pl-4 border-l border-[#E5E7E2]">
                                    <span className="flex items-center gap-1 text-[#D47A55]" data-testid="nav-streak">
                                        <Flame className="w-4 h-4" strokeWidth={1.8} />
                                        <span className="font-medium">{user.streak || 0}</span>
                                    </span>
                                    <span className="flex items-center gap-1 text-[#2D5D4E]" data-testid="nav-xp">
                                        <Sparkles className="w-4 h-4" strokeWidth={1.8} />
                                        <span className="font-medium">{user.xp || 0}</span>
                                    </span>
                                </div>
                            </>
                        ) : (
                            <span className="text-sm text-[#515B57] italic hidden md:inline" data-testid="nav-locked-notice">
                                Placement check unlocks lessons
                            </span>
                        )}
                        <a
                            href={tutoringUrl("navbar")}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackClick("navbar", "liftupliteracy.com")}
                            data-testid="nav-tutoring"
                            className="text-[#515B57] hover:text-[#D47A55] text-sm font-medium hidden sm:inline"
                        >
                            Tutoring ↗
                        </a>
                        {user?.voice && (
                            <Link
                                to="/app/profile"
                                data-testid="nav-voice-badge"
                                title={`TTS voice: ${user.voice}. Tap to change.`}
                                className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F5F1E4] border border-[#E5E7E2] text-[#515B57] text-xs font-medium hover:bg-[#E8E2D2] hover:text-[#1C2321] transition-colors"
                            >
                                <Volume2 className="w-3.5 h-3.5 text-[#2D5D4E]" strokeWidth={1.8} />
                                <span data-testid="nav-voice-name">
                                    {user.voice.charAt(0).toUpperCase() + user.voice.slice(1)}
                                </span>
                            </Link>
                        )}
                        <button
                            onClick={handleLogout}
                            data-testid="nav-logout"
                            className="text-[#515B57] hover:text-[#B24C4C] transition-colors"
                            aria-label="Log out"
                        >
                            <LogOut className="w-5 h-5" strokeWidth={1.8} />
                        </button>
                        <NavLink
                            to="/app/profile"
                            data-testid="nav-profile"
                            className={({ isActive }) =>
                                `w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                                    isActive
                                        ? "bg-[#2D5D4E] text-[#FAF9F6]"
                                        : "bg-[#E0EDE6] text-[#2D5D4E] hover:bg-[#2D5D4E] hover:text-[#FAF9F6]"
                                }`
                            }
                            aria-label="Profile"
                        >
                            <User className="w-4 h-4" strokeWidth={1.8} />
                        </NavLink>
                    </nav>
                ) : (
                    <div className="flex items-center gap-3">
                        <Link to="/login" data-testid="nav-login-link" className="text-[#515B57] hover:text-[#2D5D4E] text-base font-medium">
                            Sign in
                        </Link>
                        <Link
                            to="/register"
                            data-testid="nav-register-link"
                            onClick={() => trackClick("navbar", "internal:register")}
                            className="px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] transition-colors"
                        >
                            Start learning
                        </Link>
                    </div>
                )}
            </div>
        </header>
    );
}
